/**
 * Location stamp (SPEC §6, admin-only). Since 10 Oct 2026 (owner's decision) a working location
 * is required: the phone app checks it before Home (`getFix`, LocationGate), and Save needs a
 * fix. Tracking starts when the add screen opens and keeps the latest fix, so it is usually ready
 * by Save; if not, Save waits for one, then falls back to the last good fix of the last minutes.
 *
 * Browsers only give the location to secure pages (https, or localhost on the same computer):
 * a phone opening the dev server at http://192.168.x.x always gets null.
 */
import type { GeoStamp } from '../api/types.ts';

/** How long Save waits for a first fix it doesn't have yet. */
export const SETTLE_MS = 6000;

export interface Locator {
  /** The latest fix, or null. */
  current(): GeoStamp | null;
  /** The latest fix, waiting up to `waitMs` for the first one (never waits once refused). */
  settle(waitMs?: number): Promise<GeoStamp | null>;
  stop(): void;
}

type Geo = Pick<Geolocation, 'getCurrentPosition' | 'watchPosition' | 'clearWatch'>;

function toStamp(pos: GeolocationPosition): GeoStamp {
  return {
    lat: pos.coords.latitude,
    lng: pos.coords.longitude,
    accuracyM: Math.round(pos.coords.accuracy),
    locatedAt: new Date(pos.timestamp).toISOString(),
  };
}

const PERMISSION_DENIED = 1;

function browserGeo(): Geo | undefined {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator ? navigator.geolocation : undefined;
}

function secureContext(): boolean {
  return typeof window === 'undefined' || window.isSecureContext;
}

export function startLocating(geo: Geo | undefined = browserGeo(), secure: boolean = secureContext()): Locator {
  let stamp: GeoStamp | null = null;
  let refused = !geo || !secure;
  let waiters: ((s: GeoStamp | null) => void)[] = [];
  const flush = () => {
    const w = waiters;
    waiters = [];
    for (const resolve of w) resolve(stamp);
  };
  const onFix = (pos: GeolocationPosition) => {
    const next = toStamp(pos);
    // A precise GPS fix is not replaced by a rough network one that arrives after it.
    if (!stamp || next.accuracyM <= stamp.accuracyM * 2 || Date.parse(next.locatedAt) - Date.parse(stamp.locatedAt) > 60_000) {
      stamp = next;
    }
    flush();
  };
  const onError = (err: GeolocationPositionError) => {
    if (err.code === PERMISSION_DENIED) {
      refused = true;
      flush();
    }
    // Timeout or no signal: the watch keeps trying.
  };

  let watchId: number | null = null;
  if (geo && !refused) {
    // A quick rough fix (wifi / cell) first, then GPS keeps it fresh while the form is open.
    geo.getCurrentPosition(onFix, onError, { enableHighAccuracy: false, timeout: 10_000, maximumAge: 120_000 });
    watchId = geo.watchPosition(onFix, onError, { enableHighAccuracy: true, timeout: 30_000, maximumAge: 60_000 });
  }

  return {
    current: () => stamp,
    settle(waitMs = SETTLE_MS) {
      if (stamp || refused) return Promise.resolve(stamp);
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          waiters = waiters.filter((w) => w !== done);
          resolve(stamp);
        }, waitMs);
        const done = (s: GeoStamp | null) => {
          clearTimeout(timer);
          resolve(s);
        };
        waiters.push(done);
      });
    },
    stop() {
      if (geo && watchId !== null) geo.clearWatch(watchId);
      watchId = null;
      flush();
    },
  };
}


/* ---------------------------------------------------------------- the check before Home */

/** Why no usable location: each has its own message and help in the app. */
export type FixProblem = 'insecure' | 'unsupported' | 'denied' | 'off' | 'timeout' | 'weak';

export type FixResult = { ok: true; stamp: GeoStamp } | { ok: false; problem: FixProblem };

/** Coarser than this (metres) is too rough to tell where something was bought. */
export const MAX_ACCURACY_M = 1500;

let lastGood: { stamp: GeoStamp; at: number } | null = null;

/** The last good fix (from the check or a save), if it is recent enough. */
export function recentFix(maxAgeMs: number, now: number = Date.now()): GeoStamp | null {
  return lastGood && now - lastGood.at <= maxAgeMs ? lastGood.stamp : null;
}

export function rememberFix(stamp: GeoStamp, now: number = Date.now()): void {
  lastGood = { stamp, at: now };
}

function once(geo: Geo, options: PositionOptions): Promise<GeoStamp | number> {
  return new Promise((resolve) => {
    geo.getCurrentPosition(
      (pos) => resolve(toStamp(pos)),
      (err) => resolve(err.code),
      options,
    );
  });
}

/**
 * One fresh location for the check before Home: GPS first, then (older phones, indoors) the
 * network's rough position. Never rejects: a problem says what to fix.
 */
export async function getFix(geo: Geo | undefined = browserGeo(), secure: boolean = secureContext()): Promise<FixResult> {
  if (!secure) return { ok: false, problem: 'insecure' };
  if (!geo) return { ok: false, problem: 'unsupported' };
  let got = await once(geo, { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 });
  if (typeof got === 'number' && got !== PERMISSION_DENIED) {
    // No GPS fix in time / no signal: the network's position is often enough.
    got = await once(geo, { enableHighAccuracy: false, timeout: 10_000, maximumAge: 120_000 });
  }
  if (typeof got === 'number') {
    return { ok: false, problem: got === PERMISSION_DENIED ? 'denied' : got === 2 ? 'off' : 'timeout' };
  }
  if (got.accuracyM > MAX_ACCURACY_M) return { ok: false, problem: 'weak' };
  rememberFix(got);
  return { ok: true, stamp: got };
}
